import {
  type ComputedRef,
  computed,
  type MaybeRefOrGetter,
  toValue,
} from 'vue';
import type {
  ImageGridFit,
  ImageGridSizing,
} from '@/ui-kit/components/image-grid/types';
import {
  type ClassicPortraitGalleryLayout,
  type ClassicPortraitGalleryOrientation,
  type ClassicPortraitGalleryPages,
  type ClassicPortraitGalleryRail,
  GROUP_TILE_SCALE,
  type KioskClassicGalleryLayout,
  PORTRAIT_TILE_RATIO,
  parseClassicPortraitGalleryLayout,
} from './classic-mixed-portrait-gallery';
import type { PortraitGridLayoutState } from './usePortraitGridLayout';

export type { KioskClassicGalleryLayout };

// DEV-only seam: visual discovery drives every gallery combination through
// `window.__classicMixedGalleryOverride`.
export interface ClassicGalleryOverride {
  filter?: boolean;
  // true forces the mixed gallery, false the legacy components.
  force?: boolean;
  layout?: ClassicPortraitGalleryLayout;
  orientation?: ClassicPortraitGalleryOrientation;
  rail?: Partial<ClassicPortraitGalleryRail>;
  showPreview?: boolean;
}

export interface ClassicGalleryConfig {
  enabled: ComputedRef<boolean>;
  filter: boolean;
  layout: ComputedRef<ClassicPortraitGalleryLayout>;
  orientation: ComputedRef<ClassicPortraitGalleryOrientation>;
  pages: ComputedRef<ClassicPortraitGalleryPages | undefined>;
  rail: Partial<ClassicPortraitGalleryRail> | undefined;
  // null: the layout decides.
  showPreview: boolean | null;
}

interface UseClassicGalleryConfigOptions {
  grid: PortraitGridLayoutState;
  isVerticalKiosk: MaybeRefOrGetter<boolean>;
  // kiosks.classic_gallery_layout — per-kiosk choice, wins over the build.
  kioskLayout?: MaybeRefOrGetter<string | null | undefined>;
  portraitsPerSlide: number;
  viewportIsPortrait: MaybeRefOrGetter<boolean>;
}

// ImageGrid re-declares the --ImageGallery--* variables on every cell, so a
// cell fed `var(--ImageGallery--item-height)` would reference itself and go
// invalid; the legacy presets read these aliases the gallery root defines.
const LEGACY_CELL: Pick<
  ClassicPortraitGalleryPages,
  'cellAspectRatio' | 'cellHeight' | 'cellRadius' | 'cellWidth' | 'fit' | 'gap'
> = {
  cellAspectRatio: PORTRAIT_TILE_RATIO,
  cellHeight: 'var(--cmg-legacy-item-height)',
  cellRadius: 'var(--cmg-legacy-item-radius)',
  cellWidth: 'var(--cmg-legacy-item-width)',
  fit: 'cover',
  gap: 'var(--cmg-legacy-item-gap)',
};

function readOverride(): ClassicGalleryOverride {
  if (!import.meta.env.DEV) {
    return {};
  }
  const host = window as Window & {
    __classicMixedGalleryOverride?: ClassicGalleryOverride;
  };
  return host.__classicMixedGalleryOverride ?? {};
}

// The mixed gallery is the only gallery now; by default it renders the old
// paged layouts. Two ways back to the legacy components while the rollout
// settles: VITE_CLASSIC_MIXED_GALLERY_ENABLED=false reverts every kiosk of a
// build, kiosks.classic_gallery_layout = "legacy" reverts one location. The
// layout comes from the kiosk config first (one location at a time), the
// build default VITE_CLASSIC_MIXED_GALLERY_LAYOUT second.
function resolveLayout(
  kioskLayout: string | null | undefined
): ClassicPortraitGalleryLayout {
  return (
    parseClassicPortraitGalleryLayout(kioskLayout) ??
    parseClassicPortraitGalleryLayout(
      import.meta.env.VITE_CLASSIC_MIXED_GALLERY_LAYOUT
    ) ??
    'pages'
  );
}

export function useClassicGalleryConfig(
  options: UseClassicGalleryConfigOptions
): ClassicGalleryConfig {
  const override = readOverride();
  const enabled = computed(
    () =>
      override.force ??
      (toValue(options.kioskLayout) !== 'legacy' &&
        import.meta.env.VITE_CLASSIC_MIXED_GALLERY_ENABLED !== 'false')
  );
  // Computed rather than read once: the kiosk config can be refreshed after
  // the step mounted, and a stale layout would outlive it until remount.
  const layout = computed<ClassicPortraitGalleryLayout>(
    () => override.layout ?? resolveLayout(toValue(options.kioskLayout))
  );
  const orientation = computed<ClassicPortraitGalleryOrientation>(
    () =>
      override.orientation ??
      (toValue(options.viewportIsPortrait) ? 'portrait' : 'landscape')
  );

  // Paged mode hands the gallery the exact geometry the legacy galleries
  // rendered with, so the old kiosk layouts survive the switch unchanged.
  const pages = computed<ClassicPortraitGalleryPages | undefined>(() => {
    if (layout.value !== 'pages') {
      return undefined;
    }
    const { grid } = options;
    const arrowScale = grid.portraitArrowScale.value;
    if (!toValue(options.isVerticalKiosk)) {
      const columns = toValue(options.viewportIsPortrait)
        ? 1
        : options.portraitsPerSlide;
      return {
        ...LEGACY_CELL,
        arrowScale,
        groupScale: GROUP_TILE_SCALE,
        layout: { columns, rows: 1 },
        preset: 'carousel',
        sizing: 'width-fixed',
      };
    }
    if (!grid.useImageGrid.value) {
      // The legacy vertical grid fills column-major (top-left, bottom-left,
      // then the right column), so a still-generating portrait sits where
      // the old gallery put it.
      return {
        ...LEGACY_CELL,
        arrowScale,
        flow: 'column',
        groupScale: GROUP_TILE_SCALE,
        layout: { columns: 2, rows: 2 },
        preset: 'grid',
        sizing: 'height-fixed',
      };
    }
    return {
      arrowScale,
      cellAspectRatio: grid.portraitCellAspect.value,
      cellHeight: grid.portraitCellHeight.value,
      cellRadius: grid.portraitCellRadius.value,
      cellWidth: grid.portraitCellWidth.value,
      fit: grid.portraitFit.value as ImageGridFit,
      gap: grid.portraitGridGap.value,
      groupScale: GROUP_TILE_SCALE,
      layout: grid.galleryLayout.value,
      preset: 'image-grid',
      sizing: grid.portraitGridSizing.value as ImageGridSizing,
    };
  });

  return {
    enabled,
    filter: override.filter ?? true,
    layout,
    orientation,
    pages,
    rail: override.rail,
    showPreview: override.showPreview ?? null,
  };
}
